<template>
  <div>
    <!-- Custom Tab Navigation -->
    <div class="flex gap-8 mb-6">
      <button
        :class="[
          'px-[16px] py-[10px] rounded-full font-medium transition-colors',
          activeTab === 'withdrawal'
            ? 'bg-primary text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
        :disabled="isLoading"
        @click="switchTab('withdrawal')"
      >
        Penarikan Barang
      </button>
      <button
        :class="[
          'px-[16px] py-[10px] rounded-full font-medium transition-colors',
          activeTab === 'damaged'
            ? 'bg-primary text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
        :disabled="isLoading"
        @click="switchTab('damaged')"
      >
        Barang Rusak
      </button>
    </div>

    <!-- Tab Content -->
    <div>
      <div
        v-if="activeTab === 'withdrawal'"
        class="mb-[8px] text-[#333333] flex items-center justify-start gap-[8px] [&_p]:mb-0"
      >
        <p>Revenue dari Biaya Penarikan</p>
        <p>:
          <b-spinner
            v-if="summary.is_loading"
            small
            variant="primary"
            class="mb-[2px]"
          />
          <span v-else>{{ summary.revenue_withdrawal | rupiah }}</span>
        </p>
      </div>
      <div class="relative">
        <b-table
          :empty-text="isLoading ? '' : 'Tidak ada data yang ditampilkan.'"
          responsive
          show-empty
          sticky-header
          class="!max-h-[50vh] !mb-0"
          :items="items"
          :fields="fieldsPenarikan"
          @scroll.native="handleScroll"
        >
          <template #cell(status)="data">
            <div
              :class="[
                'px-[8px] py-[2px] text-center rounded-[100px]',
                handleStatus('class', data.item.status)]"
            >
              {{ handleStatus('text', data.item.status, data.item.type) }}
            </div>
          </template>
          <template #cell(outbound_id)="data">
            <router-link
              :to="`/kompack/total-nilai-transaksi/penarikan/${data.item.outbound_id}`"
              class="text-[#F95031] hover:underline"
            >
              Lihat Detail
            </router-link>
          </template>
        </b-table>

        <!-- Loading indicator -->
        <div
          v-if="isLoading"
          class="absolute bottom-0 left-0 right-0 flex justify-center items-center p-[20px] bg-white bg-opacity-90"
        >
          <div class="flex items-center gap-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            <span class="text-sm text-gray-600">Memuat data...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { YEAR_MONTH_DAY } from '@/libs/filterDate'
import buildQueryString from '../helpers/buildQueryString'
import { fieldsPenarikan } from '../config'

/**
 * Component untuk menampilkan card data penarikan dengan tabs
 * @component PenarikanCard
 */
export default {
  name: 'TransaksiBiayaFulfillmentCard',
  props: {
    /** @type {Object} Periode tanggal untuk filter data */
    period: {
      type: Object,
      required: true,
    },
    /** @type {Object} Summary object containing withdrawal revenue and loading state */
    summary: {
      type: Object,
      default: () => ({
        revenue_withdrawal: 0,
        is_loading: true,
      }),
    },
  },
  data() {
    return {
      activeTab: this.$route.query.tab || 'withdrawal',
      dataFetcher: null,
    }
  },
  computed: {
    /**
     * Data items dari fetcher
     * @returns {Array} Array data penarikan
     */
    items() {
      return this.dataFetcher && this.dataFetcher.data ? this.dataFetcher.data : []
    },
    /**
     * Status loading dari fetcher
     * @returns {boolean} Status loading
     */
    isLoading() {
      return this.dataFetcher && this.dataFetcher.isLoading ? this.dataFetcher.isLoading : false
    },
  },
  watch: {
    period: {
      handler() {
        this.handleRefreshData()
      },
    },
  },
  created() {
    this.fieldsPenarikan = fieldsPenarikan
    this.initializeFetcher()
  },
  mounted() {
    this.handleRefreshData()
  },
  methods: {
    /**
     * Switch ke tab yang dipilih dan fetch data baru
     * @param {string} tabName - Nama tab (withdrawal/damaged)
     */
    switchTab(tabName) {
      this.$router.push(buildQueryString({ tab: tabName }, this.$route.query))
      this.$nextTick(() => {
        this.dataFetcher.fetchData({
          reset: true,
          options: {
            type: tabName,
            start_date: YEAR_MONTH_DAY(this.period.startDate),
            end_date: YEAR_MONTH_DAY(this.period.endDate),
          },
        })
        this.activeTab = tabName
      })
    },
    initializeFetcher() {
      const fnFetch = ({
        offset, limit, start_date, end_date, type,
      }) => this.$http_new.get('/komship/api/v1/admin/outbounds/withdrawal', {
        params: {
          offset,
          limit,
          start_date,
          end_date,
          type,
        },
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => ({
        data: [],
      }))

      this.dataFetcher = createPaginatedFetcher(fnFetch, 0, 50, this.period.startDate, this.period.endDate, this.activeTab)
    },
    async handleRefreshData() {
      await this.dataFetcher.fetchData({
        reset: true,
        options: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
          type: this.activeTab,
        },
      })
    },
    /**
     * Handle scroll untuk infinite loading
     * @param {Event} event - Event scroll
     */
    handleScroll(event) {
      if (event.target.scrollTop + event.target.clientHeight + 500 >= event.target.scrollHeight) {
        if (this.dataFetcher.data.length <= 0) return
        this.dataFetcher.fetchData()
      }
    },
    /**
     * Handle status display (class atau text)
     * @param {string} part - 'class' atau 'text'
     * @param {string} status - Status penarikan
     * @returns {string} CSS class atau text sesuai status
     */
    handleStatus(part, status, type) {
      if (part === 'class') {
        if (status === 'Diajukan') return 'text-[#FBA63C] bg-[#FFF2E2]'
        if (status === 'Selesai') return 'text-[#4285F4] bg-[#DFF3FF]'
        if (status === 'Ditolak') return 'text-[#E31A1A] bg-[#FFEDED]'
        if (status === 'Diproses') return 'text-[#34A770] bg-[#DCF3EB]'
      }
      if (status === 'Diajukan' && type === 'Barang Rusak') return 'Menunggu respon partner'
      if (status === 'Diajukan' && type === 'Penarikan Barang') return 'Menunggu respon mitra'
      if (status === 'Diproses' && type === 'Barang Rusak') return 'Disetujui'
      return status
    },
  },
}
</script>
